import { NumberFormatSeparator } from 'enums-lib';

/**
 * The formatting options a number field carries. These shapes are stored three ways and must stay
 * in step: on the field itself inside the Word template, on the corp config in DynamoDB
 * (`IntegerFormatOptions` / `DecimalFormatOptions` in `awesomedocs-v2-core`), and in the Manager's
 * corp defaults UI.
 *
 * `minimumIntegerDigits` is deliberately not exposed anywhere in the UI: the add-in's format panels
 * validate against the system default and silently reject anything else.
 */
export type IntegerFormatOptions = {
  minimumIntegerDigits: number;
  thousandsSeparator: NumberFormatSeparator;
};

export type DecimalFormatOptions = {
  minimumIntegerDigits: number;
  thousandsSeparator: NumberFormatSeparator;
  exactDecimalDigits: number;
  decimalSeparator: NumberFormatSeparator;
  isPercentage?: boolean; // Absent on templates authored before this shipped, which reads as OFF.
};

/**
 * The fallback formatting applied to a number field when neither the field itself nor the corp
 * config specifies anything. The Word add-in falls back to these at insert time; the Manager seeds
 * its corp defaults form from them so a corp that has never saved sees the same values the add-in
 * would have used anyway.
 *
 * Frozen because callers spread from them rather than copying defensively.
 *
 * Note the two thousands separators differ on purpose: an integer defaults to no separator, a
 * decimal to a comma. The Manager mirrors only the decimal defaults, and takes the integer
 * thousands separator from what the admin picks, so do not "tidy" these into agreement.
 */
export const SYSTEM_DEFAULT_INTEGER_FORMAT: Readonly<IntegerFormatOptions> = Object.freeze({
  minimumIntegerDigits: 0,
  thousandsSeparator: NumberFormatSeparator.NONE,
});

export const SYSTEM_DEFAULT_DECIMAL_FORMAT: Readonly<DecimalFormatOptions> = Object.freeze({
  minimumIntegerDigits: 1,
  thousandsSeparator: NumberFormatSeparator.COMMA,
  exactDecimalDigits: 2,
  decimalSeparator: NumberFormatSeparator.POINT,
});
